import React, { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
	formatValue,
	getSelectOptions
} from '@/pages/MicrocreditRequests/MicrocreditQuestionnaire/utils';
import {
	DaysPerSimulationPeriod,
	MicrocreditRequest
} from '@/graphql/__generated__/credit-requests';
import QuestionnaireInput from '@/pages/MicrocreditRequests/MicrocreditQuestionnaire/QuestionnaireInput';
import { Typography } from '@/UIlibrary/Typography/Typography';
import QuestionnaireEditableInput from '@/pages/MicrocreditRequests/MicrocreditQuestionnaire/QuestionnaireEditableInput';
import Toggle from '@/UIlibrary/Toggle/Toggle';
import { useSimulationStore } from '@/stores/useSimulationStore';

interface SimulationInputsProps {
	data: MicrocreditRequest;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	ordinaryInterestRate = 'ordinary_interest_rate',
	daysPerPeriod = 'days_per_period',
	paymentCapacity = 'payment_capacity',
	amountRequested = 'amount_requested_for_simulation',
	percentageOfCreditToSettle = 'percentage_of_credit_to_settle',
	firstPaymentDate = 'first_payment_date',
	sameDayPayment = 'same_day_payment'
}

const SimulationInputs: React.FC<SimulationInputsProps> = ({ data, inputErrors }) => {
	const t = useTranslations('MicrocreditRequestSimulation');
	const { formData, updateField, initializeFromData, resetForm } = useSimulationStore();
	const [forceUpdate, setForceUpdate] = useState(0);
	const [maximumPeriods, setMaximumPeriods] = useState(60);

	const simulationPaymentCapacity = data?.simulation?.configuration?.paymentCapacity;
	const simulationAmountRequested = data?.simulation?.configuration?.amountRequested;
	const simulationInterestRate = data?.simulation?.configuration?.ordinaryInterestRate;
	const simulationFirstPaymentDate = data?.simulation?.configuration?.firstPaymentDate;
	const microcreditRequestId = data?.microcreditRequestId;

	useEffect(() => {
		if (microcreditRequestId) {
			resetForm();
			initializeFromData(data);

			setTimeout(() => {
				setForceUpdate((prev) => prev + 1);
			}, 5);
		}
	}, [
		microcreditRequestId,
		simulationPaymentCapacity,
		simulationAmountRequested,
		simulationInterestRate,
		simulationFirstPaymentDate,
		data,
		initializeFromData,
		resetForm
	]);

	const defaultFirstPaymentDate = useMemo(() => {
		if (!formData.daysPerPeriod) return '';

		try {
			switch (formData.daysPerPeriod) {
				case DaysPerSimulationPeriod.BIWEEKLY:
					return new Date(new Date().setDate(new Date().getDate() + 15))
						.toISOString()
						.split('T')[0];
				case DaysPerSimulationPeriod.WEEKLY:
					return new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0];
				case DaysPerSimulationPeriod.MONTHLY:
					return new Date(new Date().setDate(new Date().getDate() + 30))
						.toISOString()
						.split('T')[0];
				default:
					return '';
			}
		} catch (error) {
			console.error('Error calculating default first payment date:', error);
			return '';
		}
	}, [formData.daysPerPeriod]);

	useEffect(() => {
		if (formData.daysPerPeriod) {
			const calculatedDate = defaultFirstPaymentDate;
			if (calculatedDate) {
				updateField('firstPaymentDate', calculatedDate);
			}
		}
	}, [formData.daysPerPeriod, defaultFirstPaymentDate, updateField]);

	const maximumPeriodsMap = {
		[DaysPerSimulationPeriod.WEEKLY]: 240,
		[DaysPerSimulationPeriod.BIWEEKLY]: 120,
		[DaysPerSimulationPeriod.MONTHLY]: 60
	} as const;

	useEffect(() => {
		if (formData.daysPerPeriod) {
			setMaximumPeriods(maximumPeriodsMap[formData.daysPerPeriod]);
		}
	}, [formData.daysPerPeriod]);

	const PERCENTAGE_BOUNDS = {
		MIN: 0,
		MAX: 100,
		DECIMAL_PLACES: 2
	} as const;

	const PERCENTAGE_FIELDS = new Set(['ordinaryInterestRate', 'percentageOfCreditToSettle']);

	const validateAndClampPercentage = (value: any, fieldKey: string): string => {
		const stringValue = value?.toString() || '';

		if (stringValue === '' || stringValue === '.') return stringValue;

		const numericValue = parseFloat(stringValue);

		if (isNaN(numericValue)) return value;

		if (numericValue > PERCENTAGE_BOUNDS.MAX) {
			const currentValue = formData[fieldKey as keyof typeof formData] as string;
			return currentValue || '1';
		}

		if (numericValue < 0) {
			return '0';
		}

		const factor = Math.pow(10, PERCENTAGE_BOUNDS.DECIMAL_PLACES);
		return (Math.round(numericValue * factor) / factor).toString();
	};

	const isPercentageField = (fieldKey: string): boolean => PERCENTAGE_FIELDS.has(fieldKey);

	const handleChange = (fieldKey: string, value: any): void => {
		const processedValue = isPercentageField(fieldKey)
			? validateAndClampPercentage(value, fieldKey)
			: value;

		updateField(fieldKey as keyof typeof formData, processedValue);
	};

	return (
		<div className="px-6 mt-4" key={forceUpdate}>
			<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
				<div className={`flex flex-row items-center mb-4`}>
					<div className="grow">
						<Typography weight="semibold">{t('CreditSimulation')}</Typography>
					</div>
				</div>
				<div className="flex flex-row flex-wrap">
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireInput
							title={'calculatedMonthlyNetIncome'}
							value={formatValue(data.calculatedNetIncome, t)}
							translationsSource="MicrocreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('ordinaryInterestRate', newValue)}
							title={'OrdinaryInterestRate'}
							value={formData.ordinaryInterestRate}
							translationsSource="MicrocreditRequestSimulation"
							type="number"
							error={inputErrors?.[inputErrorMapping.ordinaryInterestRate] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireInput
							title={'AmountRequested'}
							value={`${formatValue(data.creditDetails?.amountRequested, t)}`}
							translationsSource="MicrocreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							isSelect
							options={getSelectOptions('daysPerPeriod', t)}
							onChange={(newValue) => handleChange('daysPerPeriod', newValue)}
							title={'DaysPerPeriod'}
							value={formData.daysPerPeriod}
							translationsSource="MicrocreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.daysPerPeriod] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('paymentCapacity', newValue)}
							type="money"
							title={'PaymentCapacity'}
							value={formData.paymentCapacity || data.creditDetails?.amountRequested || ''}
							translationsSource="MicrocreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.paymentCapacity] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1 items-center">
						<QuestionnaireInput
							title={'MaximumPeriods'}
							value={maximumPeriods.toString()}
							translationsSource="MicrocreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('amountRequested', newValue)}
							type="money"
							title={'amountRequested'}
							value={formData.amountRequested}
							translationsSource="MicrocreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.amountRequested] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('percentageOfCreditToSettle', newValue)}
							type="number"
							title={'PercentageOfCreditToSettle'}
							value={formData.percentageOfCreditToSettle}
							translationsSource="MicrocreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.percentageOfCreditToSettle] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1 items-center">
						<div className={`flex flex-row justify-between`}>
							<Typography size="sm" weight="medium">
								{t('SameDayPayment')}:
							</Typography>
							<Toggle
								size="medium"
								defaultChecked={formData.sameDayPayment}
								onChange={(newValue) => handleChange('sameDayPayment', newValue)}
							/>
						</div>
						<Typography size="sm" weight="medium" color="error">
							{inputErrors?.[inputErrorMapping.sameDayPayment] || ''}
						</Typography>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('firstPaymentDate', newValue)}
							type="date"
							title={'FirstPaymentDate'}
							value={formData.firstPaymentDate || defaultFirstPaymentDate}
							translationsSource="MicrocreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.firstPaymentDate] || ''}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SimulationInputs;
